import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useInView, AnimatedSection, StaggeredList } from '../hooks/useAnimations';
import { 
  ArrowRight, 
  Shield, 
  BellOff, 
  Microscope, 
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  EyeOff,
  Database,
  Trash2,
  Settings
} from 'lucide-react';

const Logo = ({ variant = 'dark' }) => (
  <Link to="/" className="flex items-center gap-2 group">
    <img 
      src="/varyynt-logo.svg" 
      alt="Varyynt" 
      className={`h-8 ${variant === 'light' ? 'brightness-0 invert' : ''}`}
    />
  </Link>
);

// Old way vs New way data
const oldWay = [
  { icon: XCircle, text: 'Resumes and job boards judge you only by what you\'ve already done.' },
  { icon: XCircle, text: 'Recruiters are paid by employers, not by you — their "fit" is based on the client\'s needs, not your ideal life.' },
  { icon: XCircle, text: 'You sift through noise, stale postings, and irrelevant InMails.' },
  { icon: XCircle, text: 'Saying "I\'m open to work" often invites spam, not real options.' },
];

const newWay = [
  { icon: CheckCircle, text: 'You define what a great next role looks like — not just title and salary, but culture, stage, work style, and what you refuse to tolerate.' },
  { icon: CheckCircle, text: 'Our system learns your preferences and dealbreakers in detail.' },
  { icon: CheckCircle, text: 'We scan roles across pharma, biotech, and life sciences and score them against your criteria.' },
  { icon: CheckCircle, text: 'You only hear from us when something is genuinely close to what you described.' },
];

const passiveBenefits = [
  { icon: Clock, title: 'Set it once, let it run', description: 'Spend a few minutes telling us what "worth making a move" looks like.' },
  { icon: BellOff, title: 'We stay quiet until it matters', description: 'No daily digests, no spammy lists, no pressure.' },
  { icon: EyeOff, title: 'You stay invisible', description: 'Employers never browse our candidate database. They never see your name, title, or profile.' },
  { icon: Shield, title: 'You stay in control', description: 'When we flag a role, you decide whether to engage or ignore it. We don\'t submit you anywhere.' },
];

const howItWorks = [
  {
    step: '01',
    title: 'Tell us what "great" looks like',
    description: 'You complete a guided onboarding that feels more like a conversation than an application.',
    details: [
      'Industries, functions, and seniority you care about',
      'Company size, stage, and ownership you prefer',
      'Remote/hybrid/on-site realities that work for your life',
      'Compensation bands and equity appetite',
      'Culture, leadership, and dealbreakers in your own words',
    ],
  },
  {
    step: '02',
    title: 'We learn your future, not just your résumé',
    description: 'We combine your profile with your LinkedIn and resume, but our priority is your forward-looking preferences. Our system builds a model of:',
    details: [
      'Roles you\'re likely qualified for',
      'Roles that align with your values and constraints',
      'Roles that would be obvious "no\'s" based on what you told us',
    ],
  },
  {
    step: '03',
    title: 'We only tap you when a close match appears',
    description: 'As new life sciences roles appear, we score them against your profile. You get an alert only when a job is:',
    details: [
      'Strongly aligned with your stated preferences',
      'Avoiding your dealbreakers',
      'Within your desired compensation/work-style bands',
    ],
    note: 'If it\'s not close to what you described, you never hear about it.',
  },
];

const candidateFirstFeatures = [
  {
    icon: Shield,
    title: 'Privacy First',
    points: [
      'Your profile is never browsed by employers.',
      'No recruiter has "search access" to you.',
      'You decide if and when to share your identity.',
    ],
  },
  {
    icon: BellOff,
    title: 'No Spam, Ever',
    points: [
      'No "because you clicked once, here are 500 jobs."',
      'Only high-signal alerts when our system believes a role is worth your attention.',
    ],
  },
  {
    icon: Microscope,
    title: 'Life Sciences Only',
    points: [
      'Built specifically for pharma, biotech, medical devices, digital health, and related sectors.',
      'Our onboarding and taxonomy speak your language: R&D, clinical, regulatory, medical, commercial, HEOR, data, and more.',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Career Intelligence',
    points: [
      'Understand how the market sees your profile.',
      'Explore how tweaks to your preferences influence your opportunities.',
      'Visualize your "career curve" over time.',
    ],
    comingSoon: true,
  },
];

const whoItsFor = [
  'R&D, clinical development, and medical affairs leaders',
  'Market access, commercial, and HEOR professionals',
  'Data, AI, and analytics talent in life sciences',
  'Regulatory, safety, quality, and operations leaders',
  'High-performing individual contributors and executives who don\'t want to publicly "look for a job"',
];

const dataControlPoints = [
  { icon: Database, text: 'Comprehensive profile captures what actually matters to you, not just keywords.' },
  { icon: Settings, text: 'You can update or pause your preferences at any time.' },
  { icon: Trash2, text: 'You can delete your data and your resumes with a single action.' },
  { icon: BellOff, text: 'Notifications are configurable and minimal by default.' },
];

// Interactive Card Component with hover effects
const InteractiveCard = ({ children, className = '', hoverScale = true, hoverGlow = false }) => (
  <div 
    className={`
      transition-all duration-300 ease-out
      ${hoverScale ? 'hover:-translate-y-1 hover:shadow-lg' : ''}
      ${hoverGlow ? 'hover:shadow-evergreen-950/10' : ''}
      ${className}
    `}
  >
    {children}
  </div>
);

// Animated Icon wrapper
const AnimatedIcon = ({ icon: Icon, className = '' }) => (
  <Icon className={`transition-transform duration-300 group-hover:scale-110 ${className}`} />
);

export default function LandingPage() {
  const [heroRef, heroInView] = useInView({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button 
                  variant="ghost" 
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200" 
                  data-testid="login-nav-btn"
                >
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  className="bg-evergreen-950 hover:bg-evergreen-900 text-white shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]" 
                  data-testid="register-nav-btn"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`transition-all duration-1000 ease-out ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Social proof badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime-50 border border-lime-200 mb-6 transition-all duration-700 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex -space-x-2">
                <div className="w-6 h-6 rounded-full bg-evergreen-950 flex items-center justify-center text-white text-xs font-bold">J</div>
                <div className="w-6 h-6 rounded-full bg-lime-600 flex items-center justify-center text-white text-xs font-bold">M</div>
                <div className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs font-bold">S</div>
              </div>
              <span className="text-sm text-slate-600 font-accent">
                <span className="font-semibold text-evergreen-950">2,400+</span> life sciences professionals joined
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading text-slate-900 tracking-tight leading-[1.1] mb-5 sm:mb-6 px-2">
              Your next great role{' '}
              <span className="text-lime-600 relative inline-block">
                will find you.
                <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3 text-lime-200" viewBox="0 0 200 12" preserveAspectRatio="none">
                  <path d="M0,8 Q50,0 100,8 T200,8" fill="none" stroke="currentColor" strokeWidth="3" className="animate-[draw_1.5s_ease-out_0.5s_forwards]" style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: heroInView ? 'draw 1.5s ease-out 0.5s forwards' : 'none' }} />
                </svg>
              </span>
            </h1>
            <p 
              className={`text-lg sm:text-xl text-slate-600 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              For life sciences leaders who are selectively open. Define what "worth leaving for" looks like—we'll watch the market and only reach out when we find it.
            </p>
            
            <div 
              className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 transition-all duration-700 delay-400 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              <Link to="/register" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-evergreen-950 hover:bg-evergreen-900 text-white h-14 sm:h-14 px-8 sm:px-10 text-base font-accent font-medium shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98] group"
                  data-testid="hero-cta-btn"
                >
                  Create Your Profile
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto h-14 px-8 text-base font-accent border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all duration-200"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="how-it-works-btn"
              >
                See How It Works
              </Button>
            </div>
            
            {/* Trust badges */}
            <div 
              className={`flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-x-6 sm:gap-y-2 text-sm text-slate-500 transition-all duration-700 delay-500 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              {['100% free for candidates', 'Your profile stays private', 'Life sciences only'].map((badge, i) => (
                <div key={badge} className="flex items-center gap-2 group cursor-default">
                  <CheckCircle className="h-4 w-4 text-lime-600 transition-transform duration-300 group-hover:scale-110" />
                  <span className="transition-colors duration-200 group-hover:text-slate-700">{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-xs sm:text-sm font-mono text-lime-600 uppercase tracking-widest mb-2 sm:mb-3">The Problem</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-slate-900 mb-4 leading-tight">
              Job boards look backward.<br />
              <span className="text-lime-600">Your career moves forward.</span>
            </h2>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
            {/* Old Way */}
            <AnimatedSection delay={100}>
              <InteractiveCard className="bg-slate-50 rounded-2xl p-8 h-full" hoverScale={false}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-slate-200 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-slate-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-500 font-heading">The Old Way</h3>
                </div>
                <p className="text-sm text-slate-400 uppercase tracking-wider mb-4">Backward-looking</p>
                <StaggeredList className="space-y-4" staggerDelay={80}>
                  {oldWay.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <item.icon className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0 transition-colors duration-200 group-hover:text-slate-500" />
                      <span className="text-slate-600 transition-colors duration-200 group-hover:text-slate-700">{item.text}</span>
                    </li>
                  ))}
                </StaggeredList>
              </InteractiveCard>
            </AnimatedSection>

            {/* New Way */}
            <AnimatedSection delay={200}>
              <InteractiveCard className="bg-lime-50 rounded-2xl p-8 border border-lime-100 h-full hover:border-lime-200" hoverGlow>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-lime-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-lime-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-evergreen-950 font-heading">The New Way</h3>
                </div>
                <p className="text-sm text-lime-600 uppercase tracking-wider mb-4">Forward-looking</p>
                <StaggeredList className="space-y-4" staggerDelay={80}>
                  {newWay.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <item.icon className="h-5 w-5 text-lime-600 mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                      <span className="text-slate-700 transition-colors duration-200 group-hover:text-slate-900">{item.text}</span>
                    </li>
                  ))}
                </StaggeredList>
              </InteractiveCard>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Passive Job Seekers Section */}
      <section className="py-14 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-xs sm:text-sm font-medium text-lime-600 uppercase tracking-wider mb-2 sm:mb-3">For Passive Seekers</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 font-heading leading-tight">
              You don't have time to job hunt.<br />
              <span className="text-lime-600">That's the point.</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Varyynt is built for people who are not refreshing job boards all day.
            </p>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {passiveBenefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <InteractiveCard 
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 h-full group cursor-default hover:border-lime-200"
                  hoverGlow
                >
                  <div className="h-12 w-12 rounded-lg bg-lime-50 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-lime-100 group-hover:scale-105">
                    <AnimatedIcon icon={benefit.icon} className="h-6 w-6 text-lime-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 font-heading transition-colors duration-200 group-hover:text-evergreen-950">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </InteractiveCard>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={500} className="text-center mt-10">
            <p className="text-slate-500 max-w-2xl mx-auto">
              Whether you're casually open to the right opportunity or actively exploring options, Varyynt works on your terms, not a recruiter's quota.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-14 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-xs sm:text-sm font-medium text-lime-600 uppercase tracking-wider mb-2 sm:mb-3">The Process</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-heading">
              How it works — in three quiet steps
            </h2>
          </AnimatedSection>

          <div className="space-y-8 sm:space-y-12">
            {howItWorks.map((step, index) => (
              <AnimatedSection key={index} delay={index * 150}>
                <div className="relative group">
                  {index < howItWorks.length - 1 && (
                    <div className="absolute left-5 sm:left-6 top-14 sm:top-16 bottom-0 w-px bg-slate-200 hidden sm:block transition-colors duration-300 group-hover:bg-teal-200" />
                  )}
                  <div className="flex gap-4 sm:gap-6">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-evergreen-950 text-white flex items-center justify-center font-bold text-base sm:text-lg font-heading transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-evergreen-950/30">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1 pb-6 sm:pb-8">
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2 font-heading transition-colors duration-200 group-hover:text-evergreen-950">{step.title}</h3>
                      <p className="text-sm sm:text-base text-slate-600 mb-3 sm:mb-4">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2 group/item">
                            <CheckCircle className="h-4 w-4 text-lime-600 mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover/item:scale-110" />
                            <span className="text-slate-600 text-sm transition-colors duration-200 group-hover/item:text-slate-800">{detail}</span>
                          </li>
                        ))}
                      </ul>
                      {step.note && (
                        <p className="mt-3 sm:mt-4 text-sm text-slate-500 italic bg-slate-50 rounded-lg p-3 transition-colors duration-200 group-hover:bg-lime-50">
                          {step.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Candidate-First Section */}
      <section className="py-14 sm:py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-xs sm:text-sm font-medium text-teal-400 uppercase tracking-wider mb-2 sm:mb-3">Candidate-First</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 font-heading leading-tight">
              Your career, your rules.<br className="sm:hidden" /> We just do the legwork.
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {candidateFirstFeatures.map((feature, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <InteractiveCard 
                  className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 h-full group cursor-default hover:border-teal-500/50 hover:bg-slate-800/70"
                >
                  <div className="h-12 w-12 rounded-lg bg-lime-500/10 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-lime-500/20 group-hover:scale-105">
                    <AnimatedIcon icon={feature.icon} className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-lg font-semibold font-heading transition-colors duration-200 group-hover:text-teal-300">{feature.title}</h3>
                    {feature.comingSoon && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-lime-500/20 text-teal-400 transition-colors duration-200 group-hover:bg-lime-500/30">Coming Soon</span>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {feature.points.map((point, idx) => (
                      <li key={idx} className="text-slate-300 text-sm flex items-start gap-2 transition-colors duration-200 group-hover:text-slate-200">
                        <span className="text-teal-400 mt-1">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </InteractiveCard>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-medium text-lime-600 uppercase tracking-wider mb-2 sm:mb-3">Who It's For</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-heading">
              Built for serious life sciences professionals
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <InteractiveCard className="bg-slate-50 rounded-2xl p-8" hoverScale={false}>
              <StaggeredList className="space-y-4" staggerDelay={100}>
                {whoItsFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 group cursor-default">
                    <div className="h-6 w-6 rounded-full bg-lime-100 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 group-hover:bg-teal-200 group-hover:scale-110">
                      <CheckCircle className="h-4 w-4 text-lime-600" />
                    </div>
                    <span className="text-slate-700 transition-colors duration-200 group-hover:text-slate-900">{item}</span>
                  </li>
                ))}
              </StaggeredList>
            </InteractiveCard>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <p className="text-center text-slate-600 mt-8 bg-lime-50 rounded-xl p-4 border border-lime-100 transition-all duration-300 hover:border-lime-200 hover:shadow-md">
              <span className="font-medium text-evergreen-950">If you wouldn't put "Open to Work" on your profile</span> — but you would take the right call — <span className="font-medium text-evergreen-950">this is for you.</span>
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Data & Control Section */}
      <section className="py-14 sm:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-medium text-lime-600 uppercase tracking-wider mb-2 sm:mb-3">Data & Control</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-heading">
              Your career data stays under your control
            </h2>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 gap-4">
            {dataControlPoints.map((point, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <InteractiveCard className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex items-start gap-4 group hover:border-lime-200" hoverGlow>
                  <div className="h-10 w-10 rounded-lg bg-lime-50 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-lime-100 group-hover:scale-105">
                    <AnimatedIcon icon={point.icon} className="h-5 w-5 text-lime-600" />
                  </div>
                  <p className="text-slate-700 transition-colors duration-200 group-hover:text-slate-900">{point.text}</p>
                </InteractiveCard>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={500} className="text-center mt-10">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-evergreen-950 hover:bg-evergreen-900 text-white h-12 px-8 shadow-lg shadow-evergreen-950/20 hover:shadow-xl hover:shadow-evergreen-950/30 transition-all duration-300 active:scale-[0.98] group"
                data-testid="data-section-cta-btn"
              >
                Create Your Private Profile
                <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 bg-slate-900 relative overflow-hidden">
        {/* Subtle background animation */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-lime-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-evergreen-950/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 font-heading leading-tight">
              Let the right role find you — without changing your status to "looking"
            </h2>
            <p className="text-base sm:text-lg text-slate-300 mb-8 sm:mb-10 max-w-2xl mx-auto">
              Join life sciences professionals who want leverage, not noise. Tell us what a great next step looks like, and let Varyynt quietly work in the background until it's worth your attention.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Link to="/register" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-lime-500 hover:bg-teal-400 text-white h-12 px-6 sm:px-8 text-base shadow-lg shadow-evergreen-950/30 hover:shadow-xl hover:shadow-evergreen-950/40 transition-all duration-300 active:scale-[0.98] group"
                  data-testid="final-cta-btn"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto h-12 px-6 sm:px-8 text-base border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-500 transition-all duration-200"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-how-it-works-btn"
              >
                See How It Works
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 group">
              <img 
                src="/varyynt-logo.svg" 
                alt="Varyynt" 
                className="h-6 brightness-0 invert"
              />
              <span className="text-slate-500 text-sm hidden sm:inline">
                — Your AI career agent that only calls when it's worth it.
              </span>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Varyynt. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* CSS for underline animation */}
      <style>{`
        @keyframes draw {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
}
